import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PHONE_CODES } from "@/lib/phoneCodes";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { CityInput, countryCodeOf } from "@/components/CityInput";
import { ClientSuggest } from "@/components/ClientSuggest";
import { PhoneWarn } from "@/components/PhoneWarn";
import { ClientTypeField, clientTypeOf } from "@/components/ClientTypeField";
import { useAuth } from "@/context/AuthContext";
import { ensureDraftId } from "@/lib/pendingQuote";

const EMPTY = { name: "", firstname: "", company: "", email: "", phone: "", phone_code: "+33", postal: "", city: "", country: "", address: "", client_type: "", siret: "" };

export default function ClientStep() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { user } = useAuth();
  const sellerCountry = (user?.sectors || [])[0]?.country || localStorage.getItem("fautras_country") || "FR";
  // New configuration: always start with empty client details (country of the seller, editable)
  const [client, setClientState] = useState(() => {
    localStorage.removeItem("fautras_client");
    localStorage.removeItem("fautras_draft_id");
    localStorage.removeItem("fautras_client_id");
    return { ...EMPTY, country: sellerCountry };
  });
  const setField = (k, v) => { setClientState((c) => ({ ...c, [k]: v, ...(k === "company" && v.trim() ? { client_type: "societe" } : {}) })); if (k === "name") localStorage.removeItem("fautras_client_id"); };
  const pick = (c) => {
    setClientState({ ...EMPTY, name: c.name || "", firstname: c.firstname || "", company: c.company || "", client_type: c.client_type || "", siret: c.siret || "", email: c.email || "", phone: c.phone || "", phone_code: c.phone_code || "+33", postal: c.postal || "", city: c.city || "", address: c.address || "", country: c.country || sellerCountry });
    localStorage.setItem("fautras_client_id", c.id);
  };

  const saveAndContinue = () => {
    const hasInfo = Object.entries(client).some(([k, v]) => !["phone_code", "country", "client_type"].includes(k) && String(v || "").trim());
    if (!hasInfo) {
      localStorage.removeItem("fautras_client");
      localStorage.removeItem("fautras_draft_id");
      nav("/selection");
      return;
    }
    localStorage.setItem("fautras_client", JSON.stringify(client));
    // Ne bloque jamais le passage au choix du modèle (connexion lente) : l'id du devis est créé sur l'appareil
    const id = ensureDraftId();
    api.post("/quotes/draft", { id, client }, { timeout: 15000 }).catch(() => {});
    nav("/selection");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-center max-w-xl mx-auto mb-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">{t("Étape 1 / 2")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Coordonnées client")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("Renseignez les informations du client, ou laissez vide et continuez directement vers le choix du modèle.")}
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="client-step-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ClientTypeField client={client} t={t} testid="client-top" hideSiret onChange={(p) => setClientState((c) => ({ ...c, ...p }))} />
            <div className="relative"><Label>{t("Nom")}</Label><Input value={client.name} onChange={(e) => setField("name", e.target.value)} data-testid="client-name-top" className="mt-1.5" autoComplete="off" />
              <ClientSuggest query={client.name} onPick={pick} t={t} /></div>
            <div><Label>{t("Prénom")}</Label><Input value={client.firstname} onChange={(e) => setField("firstname", e.target.value)} data-testid="client-prenom-top" className="mt-1.5" /></div>
            <div><Label>{t("Société")}</Label><Input value={client.company} onChange={(e) => setField("company", e.target.value)} data-testid="client-company-top" className="mt-1.5" />
              {clientTypeOf(client) === "societe" && <div className="mt-2"><Label className="text-xs">{t("SIRET")}</Label>
                <Input value={client.siret || ""} inputMode="numeric" maxLength={17} onChange={(e) => setField("siret", e.target.value)} className="mt-1" data-testid="client-top-siret" /></div>}
            </div>
            <div><Label>{t("Email")}</Label><Input type="email" value={client.email} onChange={(e) => setField("email", e.target.value)} data-testid="client-email-top" className="mt-1.5" /></div>
            <div><Label>{t("Téléphone")}</Label>
              <div className="mt-1.5 flex gap-2">
                <select value={client.phone_code || "+33"} onChange={(e) => setField("phone_code", e.target.value)} data-testid="client-phonecode-top" className="h-10 rounded-md border border-input bg-background px-2 text-sm w-24 shrink-0">
                  {PHONE_CODES.map((p) => <option key={p.c} value={p.c}>{p.l} {p.c}</option>)}
                </select>
                <Input value={client.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="client-phone-top" className="flex-1" />
              </div>
              <PhoneWarn phone={client.phone} code={client.phone_code} testid="client-phone-top-warn" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3"><Label>{t("Adresse")}</Label><Input value={client.address} onChange={(e) => setField("address", e.target.value)} data-testid="client-address-top" className="mt-1.5" /></div>
            <div><Label>{t("Code postal")}</Label><Input value={client.postal} onChange={(e) => setField("postal", e.target.value)} data-testid="client-postal-top" className="mt-1.5" /></div>
            <div><Label>{t("Ville")}</Label><CityInput value={client.city} onChange={(v) => setField("city", v)} postal={client.postal} countryCode={countryCodeOf(client.country, localStorage.getItem("fautras_country") || "FR")} testid="client-city-top" className="mt-1.5" /></div>
            <div><Label>{t("Pays")}</Label><Input value={client.country} onChange={(e) => setField("country", e.target.value)} data-testid="client-country-top" className="mt-1.5" /></div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-end gap-3 mt-5">
          <Button onClick={saveAndContinue} data-testid="client-save-btn" className="gap-2 w-full sm:w-auto">
            {t("Continuer")} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
